<template>
  <v-app id="inspire" style="background:#F9F9F9;">
    <vue-header-admin></vue-header-admin>
    <v-main class="mb-10">
      <v-container fluid class="px-6 py-6">
        <v-row>
          <v-col cols="12" md="8">
            <h1 class="text-h4 font-weight-bold text-typo mb-4">
              Scraped Vehicles
              <span v-if="run" class="text-h6 font-weight-normal text--secondary">
                - {{ formatDate(run.started_at) }}
              </span>
            </h1>
          </v-col>
          <v-col cols="12" md="4" class="text-right">
            <v-btn color="primary" @click="$router.go(-1)">
              <v-icon left class="material-icons-round">arrow_back</v-icon>
              Back to History
            </v-btn>
          </v-col>
        </v-row>

        <!-- Run Info Card -->
        <v-row v-if="run">
          <v-col cols="12">
            <v-card class="card-shadow border-radius-xl mb-4">
              <v-card-title>Run Information</v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="2">
                    <strong>Status:</strong>
                    <v-chip :color="getRunStatusColor(run.status)" small class="ml-2">
                      {{ run.status.charAt(0).toUpperCase() + run.status.slice(1) }}
                    </v-chip>
                  </v-col>
                  <v-col cols="12" md="3">
                    <strong>Started:</strong> {{ formatDate(run.started_at) }}
                  </v-col>
                  <v-col cols="12" md="2">
                    <strong>Found:</strong> {{ run.vehicles_found || 0 }}
                  </v-col>
                  <v-col cols="12" md="2">
                    <strong>Inserted:</strong> {{ run.vehicles_inserted || 0 }}
                  </v-col>
                  <v-col cols="12" md="2">
                    <strong>Updated:</strong> {{ run.vehicles_updated || 0 }}
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Vehicles Table -->
        <v-row>
          <v-col cols="12">
            <v-card class="card-shadow border-radius-xl mb-4">
              <v-card-title class="d-flex justify-space-between">
                <span>Scraped Vehicles ({{ vehicles.length }})</span>
                <div class="d-flex align-center">
                  <v-select
                    v-model="processedFilter"
                    :items="processedOptions"
                    label="Filter by Status"
                    dense
                    outlined
                    style="max-width: 200px; margin-right: 16px;"
                    @change="filterVehicles"
                  ></v-select>
                  <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Search vehicles..."
                    single-line
                    hide-details
                    dense
                    outlined
                    style="max-width: 300px;"
                  ></v-text-field>
                </div>
              </v-card-title>

              <v-card-text>
                <v-data-table
                  :headers="vehicleHeaders"
                  :items="filteredVehicles"
                  :search="search"
                  :loading="loading"
                  class="elevation-1"
                  :items-per-page="25"
                  sort-by="created_at"
                  sort-desc
                >
                  <template v-slot:item.vehicle_info="{ item }">
                    <div class="d-flex align-center">
                      <v-avatar size="40" class="mr-3" v-if="getPrimaryImage(item)">
                        <v-img :src="getPrimaryImage(item)" alt="Vehicle" contain></v-img>
                      </v-avatar>
                      <v-avatar size="40" class="mr-3" color="grey lighten-2" v-else>
                        <v-icon>directions_car</v-icon>
                      </v-avatar>
                      <div>
                        <div class="font-weight-medium">{{ getDisplayName(item) }}</div>
                        <div class="text-caption text--secondary" v-if="item.vin">
                          VIN: {{ item.vin }}
                        </div>
                        <div class="text-caption text--secondary" v-if="item.images && item.images.length">
                          {{ item.images.length }} image{{ item.images.length > 1 ? 's' : '' }}
                        </div>
                      </div>
                    </div>
                  </template>

                  <template v-slot:item.amount="{ item }">
                    <span v-if="item.amount" class="font-weight-medium">
                      ${{ Number(item.amount).toLocaleString() }}
                    </span>
                    <span v-else class="text--secondary">-</span>
                  </template>

                  <template v-slot:item.odometer="{ item }">
                    <span v-if="item.odometer">
                      {{ Number(item.odometer).toLocaleString() }} mi
                    </span>
                    <span v-else class="text--secondary">-</span>
                  </template>

                  <template v-slot:item.processed="{ item }">
                    <v-chip :color="item.processed ? 'success' : 'warning'" small>
                      {{ item.processed ? 'Processed' : 'Pending' }}
                    </v-chip>
                  </template>

                  <template v-slot:item.inventory_status="{ item }">
                    <v-chip v-if="item.inventory_id" color="success" small>
                      In Inventory
                    </v-chip>
                    <v-chip v-else color="grey" small>
                      Not Added
                    </v-chip>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small @click="viewVehicleDetails(item)" v-bind="attrs" v-on="on">
                          <v-icon small class="material-icons-round">visibility</v-icon>
                        </v-btn>
                      </template>
                      <span>View Details</span>
                    </v-tooltip>

                    <v-tooltip bottom color="grey" v-if="item.inventory_id">
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small @click="viewInInventory(item)" v-bind="attrs" v-on="on">
                          <v-icon small class="material-icons-round">launch</v-icon>
                        </v-btn>
                      </template>
                      <span>View in Inventory</span>
                    </v-tooltip>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- Vehicle Details Dialog -->
      <v-dialog v-model="detailsDialog" max-width="900px">
        <v-card v-if="selectedVehicle">
          <v-card-title>
            Vehicle Details - {{ selectedVehicle.display_name }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <!-- Vehicle Images -->
              <v-col cols="12" md="6" v-if="getVehicleImages(selectedVehicle).length">
                <v-carousel height="300" hide-delimiters show-arrows-on-hover>
                  <v-carousel-item
                    v-for="(image, index) in getVehicleImages(selectedVehicle)"
                    :key="index"
                    :src="image"
                    contain
                  ></v-carousel-item>
                </v-carousel>
                <div class="text-center mt-2 text-caption">
                  {{ getVehicleImages(selectedVehicle).length }} image{{ getVehicleImages(selectedVehicle).length > 1 ? 's' : '' }} available
                </div>
              </v-col>
              
              <!-- Vehicle Info -->
              <v-col cols="12" :md="selectedVehicle.images && selectedVehicle.images.length ? 6 : 12">
                <v-list dense>
                  <v-list-item v-if="selectedVehicle.vin">
                    <v-list-item-content>
                      <v-list-item-title>VIN</v-list-item-title>
                      <v-list-item-subtitle>{{ selectedVehicle.vin }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedVehicle.dealer_name">
                    <v-list-item-content>
                      <v-list-item-title>Dealer</v-list-item-title>
                      <v-list-item-subtitle>{{ selectedVehicle.dealer_name }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedVehicle.amount">
                    <v-list-item-content>
                      <v-list-item-title>Price</v-list-item-title>
                      <v-list-item-subtitle>${{ Number(selectedVehicle.amount).toLocaleString() }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedVehicle.odometer">
                    <v-list-item-content>
                      <v-list-item-title>Mileage</v-list-item-title>
                      <v-list-item-subtitle>{{ Number(selectedVehicle.odometer).toLocaleString() }} miles</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedVehicle.exterior_color">
                    <v-list-item-content>
                      <v-list-item-title>Exterior Color</v-list-item-title>
                      <v-list-item-subtitle>{{ selectedVehicle.exterior_color }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedVehicle.interior_color">
                    <v-list-item-content>
                      <v-list-item-title>Interior Color</v-list-item-title>
                      <v-list-item-subtitle>{{ selectedVehicle.interior_color }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedVehicle.transmission">
                    <v-list-item-content>
                      <v-list-item-title>Transmission</v-list-item-title>
                      <v-list-item-subtitle>{{ selectedVehicle.transmission }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedVehicle.body_style">
                    <v-list-item-content>
                      <v-list-item-title>Body Style</v-list-item-title>
                      <v-list-item-subtitle>{{ selectedVehicle.body_style }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedVehicle.fuel_type">
                    <v-list-item-content>
                      <v-list-item-title>Fuel Type</v-list-item-title>
                      <v-list-item-subtitle>{{ selectedVehicle.fuel_type }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedVehicle.engine">
                    <v-list-item-content>
                      <v-list-item-title>Engine</v-list-item-title>
                      <v-list-item-subtitle>{{ selectedVehicle.engine }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Processing Status</v-list-item-title>
                      <v-list-item-subtitle>
                        <v-chip :color="selectedVehicle.processed ? 'success' : 'warning'" small>
                          {{ selectedVehicle.processed ? 'Processed' : 'Pending' }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  
                  <v-list-item v-if="selectedVehicle.source_url">
                    <v-list-item-content>
                      <v-list-item-title>Source URL</v-list-item-title>
                      <v-list-item-subtitle>
                        <a :href="selectedVehicle.source_url" target="_blank" class="text-decoration-none">
                          View Original Listing
                          <v-icon small>launch</v-icon>
                        </a>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-col>
              
              <!-- Raw Data -->
              <v-col cols="12" v-if="selectedVehicle.raw_data">
                <v-expansion-panels>
                  <v-expansion-panel>
                    <v-expansion-panel-header>Raw Scraped Data</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <pre style="font-size: 12px; max-height: 300px; overflow-y: auto;">{{ JSON.stringify(selectedVehicle.raw_data, null, 2) }}</pre>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="detailsDialog = false">Close</v-btn>
            <v-btn color="grey" dark v-if="selectedVehicle.inventory_id" @click="viewInInventory(selectedVehicle)">
              View in Inventory
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>
    <vue-footer-admin></vue-footer-admin>
  </v-app>
</template>

<script>
import moment from 'moment'

export default {
  name: 'ScrapedVehicles',
  data() {
    return {
      loading: false,
      search: '',
      run: null,
      vehicles: [],
      filteredVehicles: [],
      detailsDialog: false,
      selectedVehicle: null,
      processedFilter: 'all',

      processedOptions: [
        { text: 'All Vehicles', value: 'all' },
        { text: 'Processed Only', value: 'processed' },
        { text: 'Pending Only', value: 'pending' }
      ],

      vehicleHeaders: [
        { text: 'Vehicle', value: 'vehicle_info' },
        { text: 'Price', value: 'amount' },
        { text: 'Mileage', value: 'odometer' },
        { text: 'Color', value: 'exterior_color' },
        { text: 'Status', value: 'processed' },
        { text: 'Inventory', value: 'inventory_status' },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    }
  },

  mounted() {
    this.fetchScrapedVehicles()
  },

  methods: {
    async fetchScrapedVehicles() {
      this.loading = true
      const runId = this.$route.params.id
      
      try {
        const response = await axios.get(`/api/scraped-vehicles/${runId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        
        if (response.data.success) {
          this.run = response.data.run
          this.vehicles = response.data.vehicles.data || response.data.vehicles
          this.filterVehicles()
        }
      } catch (error) {
        console.error('Error fetching scraped vehicles:', error)
        this.$toast.error('Failed to load scraped vehicles')
      } finally {
        this.loading = false
      }
    },

    filterVehicles() {
      switch (this.processedFilter) {
        case 'processed':
          this.filteredVehicles = this.vehicles.filter(v => v.processed)
          break
        case 'pending':
          this.filteredVehicles = this.vehicles.filter(v => !v.processed)
          break
        default:
          this.filteredVehicles = this.vehicles
      }
    },

    viewVehicleDetails(vehicle) {
      this.selectedVehicle = vehicle
      this.detailsDialog = true
    },

    viewInInventory(vehicle) {
      if (vehicle.inventory_id) {
        this.$router.push(`/admin/vehicle-detail/${vehicle.inventory_id}`)
      }
    },

    formatDate(dateString) {
      return moment(dateString).format('MMM DD, YYYY HH:mm')
    },

    getRunStatusColor(status) {
      switch (status) {
        case 'completed': return 'success'
        case 'failed': return 'error'
        case 'started': return 'warning'
        default: return 'grey'
      }
    },

    getPrimaryImage(vehicle) {
      const images = this.getVehicleImages(vehicle)
      return images.length > 0 ? images[0] : null
    },

    getVehicleImages(vehicle) {
      if (!vehicle.images) return []

      // Handle different image formats
      if (Array.isArray(vehicle.images)) {
        return vehicle.images.map(img => {
          if (typeof img === 'string') {
            return img // Direct URL (S3 or external)
          } else if (typeof img === 'object' && img.url) {
            return img.url // Object with URL property
          } else if (typeof img === 'object' && img.base64) {
            return img.base64 // Base64 data URL
          }
          return null
        }).filter(Boolean)
      }

      return []
    },

    getDisplayName(vehicle) {
      const parts = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean)
      return parts.join(' ') || 'Unknown Vehicle'
    }
  }
}
</script>
